/**
 * Pull a bounded SECTION of source around a character offset. LoAF (and similar)
 * give a char offset into a script that is frequently MINIFIED — one enormous
 * single line — so taking the whole file, or even the whole "line", is useless.
 * We take a fixed window of characters on each side and mark where it was cut.
 */
import { decodeSourceMap, originalPositionFor, offsetToLineColumn, lineColumnToOffset, readInlineSourceMap, readExternalSourceMapUrl } from "../common/sourcemap";

export interface SourceSection {
    /** the extracted text (window around the offset, with `…` where truncated) */
    readonly text: string;
    /** absolute offset in the original source where the window starts */
    readonly start: number;
    /** offset of the caret WITHIN {@link text} (the requested position, minus start) */
    readonly caret: number;
    /** set when resolved through a sourcemap: the ORIGINAL source path this section
     *  is from (the served file was minified), the mapped symbol name, and the
     *  original 1-based line / 0-based column of the slow spot. */
    readonly sourcePath?: string;
    readonly originalName?: string;
    readonly sourceLine?: number;
    readonly sourceColumn?: number;
}

/**
 * @param source full script text
 * @param position char offset of interest (clamped into range)
 * @param pad chars to keep on EACH side of the offset
 * @param marker optional string INJECTED at the offset (e.g. a "slow frame here"
 *        marker), so the exact spot is obvious in an otherwise dense minified blob
 */
export function extractSourceSection(source: string, position: number, pad = 100, marker?: string): SourceSection {
    const pos = Math.max(0, Math.min(position, source.length));
    const start = Math.max(0, pos - pad);
    const end = Math.min(source.length, pos + pad + 1); // inclusive of the char at pos + pad after
    const body = source.slice(start, end);
    const lead = start > 0 ? "…" : "";
    const tail = end < source.length ? "…" : "";
    const caret = lead.length + (pos - start);
    const raw = `${lead}${body}${tail}`;
    const text = marker ? raw.slice(0, caret) + marker + raw.slice(caret) : raw;
    return { text, start, caret };
}

// Fetched script text, cached by URL — a script referenced by several long frames
// is fetched once. Fetch obeys the host page's CORS/CSP (chrome-ext / arbitrary
// page); a blocked or failed fetch resolves to null, never throws.
const _sourceCache = new Map<string, Promise<string | null>>();
export function fetchSourceText(url: string): Promise<string | null> {
    let p = _sourceCache.get(url);
    if (!p) {
        if (_sourceCache.size > 64) _sourceCache.clear(); // crude cap
        p = fetch(url).then((r) => (r.ok ? r.text() : null)).catch(() => null);
        _sourceCache.set(url, p);
    }
    return p;
}

export interface OriginalSource {
    /** the FULL original source text (from the map's sourcesContent) */
    readonly text: string;
    /** char offset in {@link text} for the requested generated position */
    readonly offset: number;
    /** original source path (sourceRoot + source) */
    readonly sourcePath: string;
    /** mapped symbol name at that position, if any */
    readonly name: string | null;
    /** 1-based original line, 0-based original column */
    readonly line: number;
    readonly column: number;
}

/**
 * If the (minified) script carries an INLINE sourcemap that embeds the original text,
 * resolve `position` to the WHOLE original source + the mapped offset. Returns null
 * when there's no inline map, it can't be decoded, or it has no embedded source. This
 * is the shared core of both the Code panel (wants the whole file) and
 * {@link extractOriginalSection} (wants a bounded slice). External maps: v1 no-op.
 */
/** Build an OriginalSource from a decoded map's JSON + the generated position. Null when
 *  the map can't be decoded or doesn't embed the original text at that position. */
function originalFromMapJson(mapJson: string, generated: string, position: number): OriginalSource | null {
    const map = decodeSourceMap(mapJson);
    if (!map) return null;
    const gen = offsetToLineColumn(generated, position);
    const orig = originalPositionFor(map, gen.line, gen.column);
    if (!orig || orig.sourceContent == null) return null; // need the embedded original to show it
    return {
        text: orig.sourceContent,
        offset: lineColumnToOffset(orig.sourceContent, orig.line, orig.column),
        sourcePath: orig.source,
        name: orig.name,
        line: orig.line,
        column: orig.column,
    };
}

export function resolveOriginalSource(generated: string, position: number): OriginalSource | null {
    const mapJson = readInlineSourceMap(generated);
    return mapJson ? originalFromMapJson(mapJson, generated, position) : null;
}

/**
 * Like {@link resolveOriginalSource} but for an EXTERNAL sourcemap — fetches the `.map`
 * the file references (resolved relative to `scriptUrl`). Best-effort: a blocked / 404 /
 * absent map resolves to null so the caller falls back to the minified slice. The fetch
 * is subject to the host page's CORS/CSP (a strict site can block it — a follow-up could
 * relay it through the extension service worker).
 */
export async function resolveOriginalSourceExternal(generated: string, position: number, scriptUrl: string): Promise<OriginalSource | null> {
    const mapUrl = readExternalSourceMapUrl(generated, scriptUrl);
    if (!mapUrl) return null;
    const mapJson = await fetchSourceText(mapUrl);
    return mapJson ? originalFromMapJson(mapJson, generated, position) : null;
}

function sectionFromOriginal(o: OriginalSource, pad: number, marker?: string): SourceSection {
    const section = extractSourceSection(o.text, o.offset, pad, marker);
    return { ...section, sourcePath: o.sourcePath, originalName: o.name ?? undefined, sourceLine: o.line, sourceColumn: o.column };
}

/** A bounded section of the ORIGINAL source around a generated `position` (with the
 *  marker) via an INLINE map, or null to fall back. See {@link resolveOriginalSource}. */
export function extractOriginalSection(generated: string, position: number, pad = 100, marker?: string): SourceSection | null {
    const o = resolveOriginalSource(generated, position);
    return o ? sectionFromOriginal(o, pad, marker) : null;
}

/** Fetch a script and return a bounded section around `position`, or null if the
 *  fetch failed/was blocked. Prefers the ORIGINAL source via an inline sourcemap,
 *  falling back to the minified slice. `marker` is injected at the offset. */
export async function fetchSourceSection(url: string, position: number, pad = 100, marker?: string): Promise<SourceSection | null> {
    const text = await fetchSourceText(url);
    if (text == null) return null;
    // Prefer the ORIGINAL source: an INLINE map (sync), else an EXTERNAL map (fetched).
    const original = resolveOriginalSource(text, position) ?? await resolveOriginalSourceExternal(text, position, url);
    if (original) return sectionFromOriginal(original, pad, marker);
    // No sourcemap: still give a real line:col — the char offset located in the SERVED
    // file (meaningful for dev/unminified; line 1 + huge col for a minified one-liner).
    const section = extractSourceSection(text, position, pad, marker);
    const lc = offsetToLineColumn(text, position);
    return { ...section, sourcePath: url, sourceLine: lc.line + 1, sourceColumn: lc.column };
}

/** The marker injected at the slow-frame offset in copied/reported source sections. */
export const SLOW_FRAME_MARKER = " /* <<<<< SLOW FRAME HERE >>>>> */ ";
